import { call, put, select } from 'redux-saga/effects';
import { DriveItem } from '../../../models/Drive';
import { WebSocketMessage } from '../../../models/WebSocket';
import {
  getActiveProject,
  getCurrentDriveItem,
  getCurrentFolderItem,
  getDocumentsWidgetActiveDriveItemId,
  getDocumentsWidgetActiveProjectId,
  getProject,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import { ProjectId } from '../../../models/Types';
import { driveItemCreated } from '../actions';
import { notification } from 'antd';
import i18n from '../../../i18n';
import { Button } from '@prio365/prio365-react-library';
import { apiDownloadDriveItem } from '../api';

interface ZipPayload {
  error: string | null;
  driveItem: DriveItem;
  downloadUrl?: string;
}

const renderSuccessZipNotification = (
  message: WebSocketMessage,
  projectId: string,
  groupId?: string
) => {
  const payload = message.object as ZipPayload;
  notification.success({
    duration: 10,
    message: i18n.t('common:success'),
    description: groupId ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div>
          {i18n.t('documents:successMessages.archiveZipSuccess', {
            name: payload.driveItem.name,
          })}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
          }}
        >
          <Button
            type="link"
            size="small"
            iconProp={['fal', 'arrow-down-to-line']}
            onClick={() => apiDownloadDriveItem(groupId, payload.driveItem)}
          >
            {i18n.t('documents:successMessages.actions.archiveZipDownload')}
          </Button>
          <Button
            type="link"
            size="small"
            iconProp={['fal', 'folder-tree']}
            onClick={() => {
              const port = window.location.port
                ? `:${window.location.port}`
                : '';
              window.open(
                `${window.location.protocol}//${
                  window.location.hostname
                }${port}/module/prio/projects/${
                  projectId
                }/documents/folder/${payload.driveItem.parentReference.id}`,
                '_self'
              );
            }}
          >
            {i18n.t('documents:successMessages.actions.openZippedFile')}
          </Button>
        </div>
      </div>
    ) : (
      i18n.t('documents:successMessages.archiveZipSuccess', {
        name: payload.driveItem.name,
      })
    ),
  });
};

const renderSuccessZipDownloadNotification = (
  message: WebSocketMessage,
  downloadUrl: string
) => {
  try {
    const payload = message.object as ZipPayload;
    notification.success({
      duration: 10,
      message: i18n.t('common:success'),
      description: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div>
            {i18n.t('documents:successMessages.archiveZipSuccess', {
              name: payload.driveItem.name,
            })}
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
            }}
          >
            <Button
              type="link"
              size="small"
              iconProp={['fal', 'arrow-down-to-line']}
              href={downloadUrl}
              target="_blank"
            >
              {i18n.t('documents:successMessages.actions.archiveZipDownload')}
            </Button>
          </div>
        </div>
      ),
    });
  } catch (error) {
    console.error('Error in renderSuccessZipDownloadNotification', error);
  }
};

function* handleDocumentZippedWithoutDownloadUrl(
  message: WebSocketMessage,
  projectId: string
) {
  const payload = message.object as ZipPayload;
  try {
    const activeProjectId = yield select(getActiveProject);
    const activeProjectIdInWidgetArea = yield select(
      getDocumentsWidgetActiveProjectId
    );

    const parentDriveItemId = payload.driveItem.parentReference.id;

    if (
      projectId === activeProjectId ||
      projectId === activeProjectIdInWidgetArea
    ) {
      const currentDriveItem = yield select(getCurrentDriveItem);

      if (currentDriveItem?.id === parentDriveItemId) {
        const project = yield select((state: RootReducerState) =>
          getProject(state, projectId)
        );
        const parentIsRoot =
          payload.driveItem.parentReference?.path === '/drive/root:';
        const actuelParentDriveItemId =
          parentIsRoot && project?.groupId
            ? `root-group-${project.groupId}`
            : parentDriveItemId;
        yield put(
          driveItemCreated(
            payload.driveItem,
            actuelParentDriveItemId,
            project.groupId
          )
        );
      } else {
        const currentDriveItemIdInWidgetArea = yield select(
          getDocumentsWidgetActiveDriveItemId
        );
        if (currentDriveItemIdInWidgetArea) {
          const currentDriveItemInWidgetArea = yield select(
            (state: RootReducerState) =>
              getCurrentFolderItem(state, currentDriveItemIdInWidgetArea)
          );
          if (currentDriveItemInWidgetArea?.id === parentDriveItemId) {
            const project = yield select((state: RootReducerState) =>
              getProject(state, projectId)
            );
            const parentIsRoot =
              payload.driveItem.parentReference?.path === '/drive/root:';
            const actuelParentDriveItemId =
              parentIsRoot && project?.groupId
                ? `root-group-${project.groupId}`
                : parentDriveItemId;
            yield put(
              driveItemCreated(
                payload.driveItem,
                actuelParentDriveItemId,
                project.groupId
              )
            );
          }
        }
      }
    }
    const project = yield select((state: RootReducerState) =>
      getProject(state, projectId)
    );
    renderSuccessZipNotification(message, projectId, project?.groupId);
  } catch (error) {
    console.error(
      'Error in websocket flow - handleDocumentZippedWithoutDownloadUrl',
      error
    );
  }
}

export function* handleDocumentZipped(
  message: WebSocketMessage,
  projectId: ProjectId
) {
  try {
    const payload = message.object as ZipPayload;
    if (payload.downloadUrl) {
      renderSuccessZipDownloadNotification(message, payload.downloadUrl);
    } else {
      yield call(handleDocumentZippedWithoutDownloadUrl, message, projectId);
    }
  } catch (error) {
    console.error('Error in websocket flow - handleDocumentZipped', error);
  }
}

export function handleDocumentZipping(message: WebSocketMessage) {
  try {
    const payload = message.object as DriveItem | null;
    notification.info({
      message: (
        <div style={{ fontSize: 14 }}>
          {i18n.t(
            `documents:processMessages.${payload === null ? 'archiveZipping' : 'archiveZippingWithName'}`,
            {
              name: payload?.name,
            }
          )}
        </div>
      ),
    });
  } catch (error) {
    console.error('Error in websocket flow - handleDocumentZipping', error);
  }
}

export function handleDocumentZipError(message: WebSocketMessage) {
  try {
    const payload = message.object as ZipPayload;
    notification.error({
      message: i18n.t('common:error'),
      description:
        payload.error ?? i18n.t('documents:errorMessages.archiveZipError'),
    });
  } catch (error) {
    console.error('Error in websocket flow - handleDocumentZipError', error);
  }
}
